import { Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Chart, ChartConfiguration, Plugin } from 'chart.js';
import { finalize, skip } from 'rxjs';
import { ThemeService } from 'src/app/core/services/theme.service';
import { CustomDropdownControlConfig } from 'src/app/features/reactive-forms-ui/models/custom-dropdown-control-config';
import { IFormControlConfig } from 'src/app/features/reactive-forms-ui/interfaces/form-control-config';
import {
  BreakdownItem,
  CategorizationSummary,
  ChartLegendItem,
  TicketAnalysisFilters,
  TicketAnalysisPage,
} from '../../../models/ticket-analysis.models';
import { TicketAnalysisService } from '../../../services/ticket-analysis.service';
import { ViewPersistenceService } from 'src/app/features/lifecycle-command/services/view-persistence.service';
import {
  TICKET_ANALYSIS_FILTERS_LAYOUT,
  applyFilterDropdowns,
  createTicketAnalysisFilterConfigMap,
  createTicketAnalysisFiltersForm,
  defaultDateFrom,
  extractBaseFilters,
} from '../ticket-analysis-filters.utils';
import {
  CHART_PALETTE,
  DOUGHNUT_CENTER_COLOR,
  DOUGHNUT_LABEL_MAX_CHARS,
  DOUGHNUT_MAX_ITEMS,
  DOUGHNUT_MIN_PCT_LABEL,
} from '../ticket-analysis-chart.constants';

@Component({
  selector: 'ticket-analysis-categorization',
  templateUrl: './ticket-analysis-categorization.component.html',
  styleUrls: ['./ticket-analysis-categorization.component.scss'],
  standalone: false,
})
export class TicketAnalysisCategorizationComponent implements OnInit, OnDestroy {
  loading = false;
  loadingTickets = false;
  error = '';
  summary: CategorizationSummary | null = null;
  ticketPageData: TicketAnalysisPage | null = null;

  currentPage = 1;
  pageSize = 25;

  readonly displayedColumns = ['id', 'dateEntered', 'closedDate', 'contact', 'status', 'summary'];

  typeChartData: ChartConfiguration<'doughnut'>['data'] = { labels: [], datasets: [] };
  subTypeChartData: ChartConfiguration<'doughnut'>['data'] = { labels: [], datasets: [] };
  itemChartData: ChartConfiguration<'doughnut'>['data'] = { labels: [], datasets: [] };
  doughnutOptions: ChartConfiguration<'doughnut'>['options'] = {};

  typeChartLegend: ChartLegendItem[] = [];
  subTypeChartLegend: ChartLegendItem[] = [];
  itemChartLegend: ChartLegendItem[] = [];

  chartFiltersForm = new FormGroup({
    filterByType: new FormControl<string[]>([], { nonNullable: true }),
    filterBySubType: new FormControl<string[]>([], { nonNullable: true }),
    filterByItem: new FormControl<string[]>([], { nonNullable: true }),
  });

  typeChartFilterConfig = new CustomDropdownControlConfig({
    label: 'All',
    placeholder: 'All',
    options: [],
    multiselect: true,
    collapsibleTitle: 'Type',
    reviewLabel: 'Type',
  });
  subTypeChartFilterConfig = new CustomDropdownControlConfig({
    label: 'All',
    placeholder: 'All',
    options: [],
    multiselect: true,
    collapsibleTitle: 'Sub-Type',
    reviewLabel: 'Sub-Type',
  });
  itemChartFilterConfig = new CustomDropdownControlConfig({
    label: 'All',
    placeholder: 'All',
    options: [],
    multiselect: true,
    collapsibleTitle: 'Item',
    reviewLabel: 'Item',
  });

  readonly chartPlugins: Plugin[] = [
    {
      id: 'doughnutCenterLabel',
      beforeDraw(chart: Chart) {
        if ((chart.config as any).type !== 'doughnut') return;
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        const total = ((chart.data.datasets[0]?.data ?? []) as number[]).reduce((s, v) => s + v, 0);
        if (!total) return;
        const cx = (chartArea.left + chartArea.right) / 2;
        const cy = (chartArea.top + chartArea.bottom) / 2;
        ctx.save();
        ctx.font = 'bold 16px Inter, sans-serif';
        ctx.fillStyle = DOUGHNUT_CENTER_COLOR;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(total.toLocaleString(), cx, cy);
        ctx.restore();
      },
    },
    {
      id: 'doughnutSegmentLabels',
      afterDatasetsDraw: (chart: Chart) => {
        if ((chart.config as any).type !== 'doughnut') return;
        const { ctx } = chart;
        const dataset = chart.data.datasets[0];
        if (!dataset) return;
        const values = dataset.data as number[];
        const total = values.reduce((s, v) => s + (v || 0), 0);
        if (!total) return;

        chart.getDatasetMeta(0).data.forEach((arc: any, i) => {
          const pct = Math.round(((values[i] || 0) / total) * 100);
          if (pct < DOUGHNUT_MIN_PCT_LABEL) return;
          const props = arc.getProps(['startAngle', 'endAngle', 'innerRadius', 'outerRadius', 'x', 'y'], true);
          const midAngle = props.startAngle + (props.endAngle - props.startAngle) / 2;
          const midRadius = (props.innerRadius + props.outerRadius) / 2;
          ctx.save();
          ctx.font = '400 8px Inter, sans-serif';
          ctx.fillStyle = this.pctLabelColor;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(`${pct}%`, props.x + Math.cos(midAngle) * midRadius, props.y + Math.sin(midAngle) * midRadius);
          ctx.restore();
        });
      },
    },
  ];

  private cardBackground = '#ffffff';
  private pctLabelColor = '#374151';

  filtersForm = createTicketAnalysisFiltersForm();
  valuesMap: any = this.filtersForm.value;
  filtersConfigMap: Map<string, IFormControlConfig> = createTicketAnalysisFilterConfigMap();
  readonly filtersLayoutConfig = TICKET_ANALYSIS_FILTERS_LAYOUT;

  private readonly STORAGE_KEY = 'ticket_analysis_categorization';

  constructor(
    private readonly ticketAnalysisService: TicketAnalysisService,
    private readonly themeService: ThemeService,
    private readonly destroyRef: DestroyRef,
    private viewPersistenceService: ViewPersistenceService,
  ) {}

  ngOnDestroy(): void {
    Chart.unregister(...this.chartPlugins);
  }

  ngOnInit(): void {
    this.buildChartOptions();
    this.themeService.themeSubject$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.buildChartOptions();
      if (this.summary) this.applySummary(this.summary);
    });
    this.restoreFilters();
    this.ticketAnalysisService.exportRequested$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const { filterByType, filterBySubType, filterByItem } = this.chartFiltersForm.value;
      this.ticketAnalysisService.startExport(extractBaseFilters(this.valuesMap), {
        view: 'categorization',
        filterByType: filterByType?.join(',') || undefined,
        filterBySubType: filterBySubType?.join(',') || undefined,
        filterByItem: filterByItem?.join(',') || undefined,
      });
    });
    this.chartFiltersForm.valueChanges
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadSummary());
    this.loadDropdowns();
    this.load();
  }

  updateValuesMap(valuesMap: any): void {
    this.valuesMap = valuesMap;
  }

  applyFilters(): void {
    this.saveFilters();
    this.load();
  }

  clearFilters(): void {
    this.filtersForm.reset({
      dateRange: { start: defaultDateFrom(), end: new Date() },
      site: null,
      board: null,
      contactName: null,
      status: null,
      source: null,
    });
    this.valuesMap = this.filtersForm.value;
    this.chartFiltersForm.reset({ filterByType: [], filterBySubType: [], filterByItem: [] }, { emitEvent: false });
    this.viewPersistenceService.clearFilters(this.STORAGE_KEY);
    this.load();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadTickets();
  }

  loadTickets(): void {
    this.loadingTickets = true;
    this.ticketAnalysisService
      .getTickets(this.buildFilters(), this.currentPage, this.pageSize)
      .pipe(finalize(() => (this.loadingTickets = false)))
      .subscribe({ next: (data) => (this.ticketPageData = data) });
  }

  private load(): void {
    this.currentPage = 1;
    this.loadSummary();
    this.loadTickets();
  }

  private loadSummary(): void {
    this.loading = true;
    this.error = '';
    this.ticketAnalysisService
      .getCategorization(this.buildFilters())
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (data) => {
          this.applySummary(data);
          this.refreshChartFilterOptions(data);
        },
        error: () => (this.error = 'Failed to load categorization data.'),
      });
  }

  private applySummary(data: CategorizationSummary): void {
    this.summary = data;
    this.typeChartData = this.buildChartData(data.byServiceType);
    this.subTypeChartData = this.buildChartData(data.bySubType);
    this.itemChartData = this.buildChartData(data.byItem);
    this.typeChartLegend = this.buildLegendItems(data.byServiceType);
    this.subTypeChartLegend = this.buildLegendItems(data.bySubType);
    this.itemChartLegend = this.buildLegendItems(data.byItem);
  }

  private loadDropdowns(): void {
    this.ticketAnalysisService.getFilters().subscribe({
      next: (response) => {
        this.filtersConfigMap = applyFilterDropdowns(response, this.filtersConfigMap);
      },
    });
  }

  private buildFilters(): TicketAnalysisFilters {
    const { filterByType, filterBySubType, filterByItem } = this.chartFiltersForm.value;
    return {
      ...extractBaseFilters(this.valuesMap),
      filterByType: filterByType?.length ? filterByType.join(',') : undefined,
      filterBySubType: filterBySubType?.length ? filterBySubType.join(',') : undefined,
      filterByItem: filterByItem?.length ? filterByItem.join(',') : undefined,
    };
  }

  private refreshChartFilterOptions(data: CategorizationSummary): void {
    const toOptions = (items: BreakdownItem[]) => items.map((i) => ({ name: i.name, value: i.name }));
    this.typeChartFilterConfig = new CustomDropdownControlConfig({
      ...this.typeChartFilterConfig,
      options: toOptions(data.byServiceType),
    });
    this.subTypeChartFilterConfig = new CustomDropdownControlConfig({
      ...this.subTypeChartFilterConfig,
      options: toOptions(data.bySubType),
    });
    this.itemChartFilterConfig = new CustomDropdownControlConfig({
      ...this.itemChartFilterConfig,
      options: toOptions(data.byItem),
    });
  }

  private buildLegendItems(items: BreakdownItem[]): ChartLegendItem[] {
    return items.slice(0, DOUGHNUT_MAX_ITEMS).map((item, idx) => ({
      label:
        item.name.length > DOUGHNUT_LABEL_MAX_CHARS ? item.name.slice(0, DOUGHNUT_LABEL_MAX_CHARS) + '…' : item.name,
      color: CHART_PALETTE[idx % CHART_PALETTE.length],
      count: item.ticketCount,
    }));
  }

  private buildChartData(items: BreakdownItem[]): ChartConfiguration<'doughnut'>['data'] {
    const top = items.slice(0, DOUGHNUT_MAX_ITEMS);
    return {
      labels: top.map((i) => i.name),
      datasets: [
        {
          data: top.map((i) => i.ticketCount),
          backgroundColor: top.map((_, idx) => CHART_PALETTE[idx % CHART_PALETTE.length]),
          hoverBackgroundColor: top.map((_, idx) => CHART_PALETTE[idx % CHART_PALETTE.length] + 'cc'),
          borderColor: this.cardBackground,
          hoverBorderColor: this.cardBackground,
          borderWidth: 0.5,
          hoverOffset: 4,
        },
      ],
    };
  }

  private buildChartOptions(): void {
    const isDark = this.themeService.isInDarkMode();
    this.cardBackground = isDark ? '#1b212d' : '#ffffff';
    this.pctLabelColor = isDark ? '#e5e7eb' : '#374151';
    this.doughnutOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '65%',
      plugins: { legend: { display: false }, tooltip: { enabled: true } },
    };
  }

  private saveFilters(): void {
    const dateRange = this.valuesMap?.dateRange as { start: Date; end: Date } | null;
    this.viewPersistenceService.saveFilters(this.STORAGE_KEY, {
      ...this.valuesMap,
      dateRange: dateRange
        ? { start: dateRange.start?.toISOString?.() ?? null, end: dateRange.end?.toISOString?.() ?? null }
        : null,
      chartFilters: this.chartFiltersForm.value,
    });
  }

  private restoreFilters(): void {
    const saved = this.viewPersistenceService.loadFilters(this.STORAGE_KEY);
    if (!saved) return;
    const rawRange = saved['dateRange'] as { start: string; end: string } | null;
    const start = rawRange?.start ? new Date(rawRange.start) : defaultDateFrom();
    const end = rawRange?.end ? new Date(rawRange.end) : new Date();
    const asTree = (v: unknown) =>
      v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, any>) : null;
    const asBoard = (v: unknown) => (Array.isArray(v) && v.length > 0 ? v : null);
    const restored = {
      dateRange: { start, end },
      contactName: (saved['contactName'] as string | null) ?? null,
      board: asBoard(saved['board']),
      site: asTree(saved['site']),
      status: asTree(saved['status']),
      source: asTree(saved['source']),
    };
    this.filtersForm.patchValue(restored);
    this.valuesMap = restored;
    const chartFilters = saved['chartFilters'] as {
      filterByType: string[];
      filterBySubType: string[];
      filterByItem: string[];
    } | null;
    if (chartFilters) {
      this.chartFiltersForm.patchValue(chartFilters, { emitEvent: false });
    }
  }
}
